<template>
  <div>
    <a-card title="应收欠款详情">
      <a-button slot="extra" type="primary" ghost :loading="saving" @click="goBack">
        <a-icon type="left" />返回
      </a-button>
      <a-spin :spinning="loading">
        <a-descriptions :column="3" size="small" style="margin-bottom: 16px;">
          <a-descriptions-item label="客户">{{ info.name }}</a-descriptions-item>
          <a-descriptions-item label="编号">{{ info.number }}</a-descriptions-item>
          <a-descriptions-item label="当前欠款">{{ info.arrears_amount }}</a-descriptions-item>
          <a-descriptions-item label="结算后欠款">{{ previewArrears }}</a-descriptions-item>
        </a-descriptions>
        <div style="margin-bottom: 12px;">
          <span style="color: #8a9a90;">赊账单和赊账退货可以改单价，现结的不能改。同一产品可以一次填到所有可改的行，再点保存。已经收付的现金不动。</span>
          <a-button type="primary" style="margin-left: 16px;" :loading="saving" :disabled="!canSave" @click="queueSave">保存结算价</a-button>
        </div>
        <div v-if="productBatches.length" style="margin-bottom: 16px; padding: 12px; background: #f7faf8; border-radius: 6px;">
          <div style="margin-bottom: 8px;">同一产品批量改结算价</div>
          <div v-for="item in productBatches" :key="item.key" style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
            <span style="min-width: 220px;">{{ item.name }}<span v-if="item.spec" style="color: #888;"> {{ item.spec }}</span></span>
            <span style="color: #888; width: 48px;">{{ item.count }} 行</span>
            <a-input-number
              :min="0"
              :value="batchPrices[item.key]"
              size="small"
              style="width: 120px"
              @change="(value) => setBatchPrice(item.key, value)"
            />
            <a-button size="small" type="primary" :loading="saving" @click="queueApply(item)">保存</a-button>
          </div>
        </div>
        <div v-for="entry in entries" :key="entry.rowKey" style="margin-bottom: 16px;">
          <div style="margin-bottom: 6px;">
            {{ shortDate(entry.time) }} {{ entry.type_display }} {{ entry.number }}
            <span v-if="entry.type === 'sales'" style="margin-left: 12px;">开单 {{ entry.order_amount }} · 结算 {{ orderSettleTotal(entry) }}</span>
            <span v-if="entry.type === 'sales_return'" style="margin-left: 12px;">退货 {{ orderSettleTotal(entry) }}</span>
            <span v-if="isPriceDoc(entry) && !canEdit(entry)" style="margin-left: 12px;">现结</span>
            <span style="margin-left: 12px;">{{ impactText(entry) }}</span>
          </div>
          <a-table
            v-if="entry.goods && entry.goods.length"
            size="small"
            rowKey="id"
            :columns="entry.type === 'sales' || entry.type === 'sales_return' ? saleColumns : plainColumns"
            :dataSource="entry.goods"
            :pagination="false"
          >
            <div slot="settle_price" slot-scope="value, item">
              <a-input-number
                v-if="canEdit(entry)"
                v-model="item.settle_input"
                :min="0"
                size="small"
                style="width: 110px"
              />
              <span v-else>{{ item.price }}</span>
            </div>
          </a-table>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import { arrearsReceivableLedgers, arrearsReceivableSettle } from '@/api/finance'
import { packRatioOf } from '@/utils/pack'
import NP from 'number-precision'

function cents(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100)
}

function priceUnchanged(item) {
  const input = cents(item.settle_input)
  const loaded = cents(item.price)
  return input != null && loaded != null && input === loaded
}

function discountFactor(discount) {
  const value = Number(discount)
  if (!Number.isFinite(value) || value <= 0) return 1
  return value > 1 ? value / 100 : value
}

function lineBaseQuantity(item) {
  const base = Number(item.base_quantity)
  if (Number.isFinite(base) && base > 0) return base
  const shown = Number(item.quantity)
  if (!Number.isFinite(shown) || shown <= 0) return 0
  const ratio = packRatioOf(item)
  return shown * (ratio > 1 ? ratio : 1)
}

function lineUnit(item) {
  if (priceUnchanged(item) && item.unit_price != null && item.unit_price !== '') {
    const n = Number(item.unit_price)
    if (Number.isFinite(n) && n > 0) return n
  }
  const pack = Number(item.settle_input)
  if (!Number.isFinite(pack) || pack <= 0) return 0
  const ratio = packRatioOf(item)
  return ratio > 1 ? pack / ratio : pack
}

function goodsKey(item) {
  if (item.goods_id != null && item.goods_id !== '') return `id:${item.goods_id}`
  if (item.goods != null && typeof item.goods !== 'object') return `id:${item.goods}`
  return `name:${item.goods_number || ''}|${item.goods_name || item.name || ''}|${item.goods_spec || item.spec || ''}`
}

function shortDate(val) {
  if (!val) return ''
  const s = String(val)
  const m = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
  if (m) {
    const mm = m[2].length === 1 ? `0${m[2]}` : m[2]
    const dd = m[3].length === 1 ? `0${m[3]}` : m[3]
    return `${mm}-${dd}`
  }
  return s.length >= 10 ? s.slice(5, 10) : s
}

export default {
  name: 'ArrearsReceivableDetail',
  data() {
    return {
      loading: false,
      saving: false,
      skipLeaveSave: false,
      info: {},
      entries: [],
      batchPrices: {},
      saleColumns: [
        { title: '产品', dataIndex: 'goods_name' },
        { title: '规格', dataIndex: 'goods_spec', width: 140 },
        {
          title: '数量',
          dataIndex: 'quantity',
          width: 100,
          customRender: (value, item) => `${value != null ? value : ''}${item.unit_name || ''}`,
        },
        { title: '开单价', dataIndex: 'order_price', width: 100 },
        { title: '结算单价', dataIndex: 'settle_input', width: 130, scopedSlots: { customRender: 'settle_price' } },
        {
          title: '结算金额',
          width: 110,
          customRender: (value, item) => this.lineSettleAmount(item),
        },
      ],
      plainColumns: [
        { title: '产品', dataIndex: 'goods_name' },
        { title: '规格', dataIndex: 'goods_spec', width: 140 },
        {
          title: '数量×单价',
          width: 140,
          customRender: (value, item) => {
            const qty = item.quantity != null ? `${item.quantity}${item.unit_name || ''}` : ''
            return item.price != null && item.price !== '' ? `${qty}×${item.price}` : qty
          },
        },
        { title: '金额', dataIndex: 'amount', width: 100 },
      ],
    }
  },
  computed: {
    productBatches() {
      const map = {}
      this.entries.forEach((entry) => {
        if (!this.canEdit(entry)) return
        ;(entry.goods || []).forEach((item) => {
          const key = goodsKey(item)
          if (!map[key]) {
            map[key] = {
              key,
              name: item.goods_name || item.name || '未命名产品',
              spec: item.goods_spec || item.spec || '',
              count: 0,
            }
          }
          map[key].count += 1
        })
      })
      return Object.values(map).filter((item) => item.count > 1)
    },
    canSave() {
      return this.entries.some((entry) => this.canEdit(entry) && (entry.goods || []).length)
    },
    previewArrears() {
      let delta = 0
      this.entries.forEach((entry) => {
        const change = NP.minus(this.orderSettleTotal(entry), Number(entry.total_amount) || 0)
        if (entry.type === 'sales') delta = NP.plus(delta, change)
        if (entry.type === 'sales_return') delta = NP.minus(delta, change)
      })
      return NP.round(NP.plus(Number(this.info.arrears_amount) || 0, delta), 2)
    },
  },
  methods: {
    shortDate,
    isPriceDoc(entry) {
      return entry.type === 'sales' || entry.type === 'sales_return'
    },
    canEdit(entry) {
      if (!this.isPriceDoc(entry)) return false
      if (entry.can_settle != null) return !!entry.can_settle
      return Number(entry.arrears_amount) !== 0
    },
    lineSettleAmount(item) {
      if (priceUnchanged(item) && item.amount != null && item.amount !== '') {
        return NP.round(Number(item.amount), 2)
      }
      const pack = Number(item.settle_input)
      const base = lineBaseQuantity(item)
      if (!Number.isFinite(pack) || pack <= 0 || !base) return 0
      const ratio = packRatioOf(item)
      const packQty = ratio > 1 ? base / ratio : base
      return NP.round(NP.times(pack, packQty), 2)
    },
    orderSettleTotal(entry) {
      if (entry.type !== 'sales' && entry.type !== 'sales_return') {
        return NP.round(Number(entry.total_amount) || 0, 2)
      }
      const changed = (entry.goods || []).some((item) => !priceUnchanged(item))
      if (!changed && !this.totalCollapsed(entry)) {
        return NP.round(Number(entry.total_amount) || 0, 2)
      }
      let goodsAmount = 0
      ;(entry.goods || []).forEach((item) => {
        goodsAmount = NP.plus(goodsAmount, this.lineSettleAmount(item))
      })
      if (entry.type === 'sales_return') {
        return NP.round(NP.plus(goodsAmount, entry.other_amount || 0), 2)
      }
      return NP.round(NP.plus(NP.times(goodsAmount, discountFactor(entry.discount)), entry.other_amount || 0), 2)
    },
    totalCollapsed(entry) {
      if (entry.type !== 'sales') return false
      const discount = Number(entry.discount)
      if (!(discount > 0 && discount <= 1)) return false
      let goodsAmount = 0
      ;(entry.goods || []).forEach((item) => {
        goodsAmount = NP.plus(goodsAmount, Number(item.amount) || 0)
      })
      const total = Number(entry.total_amount) || 0
      return goodsAmount > 0 && total < goodsAmount * discount * 0.05
    },
    impactText(entry) {
      if (this.isPriceDoc(entry) && !this.canEdit(entry)) return ''
      const arrears = Number(entry.arrears_amount)
      if (entry.type === 'collection') {
        return `收款 ${Math.abs(Number(entry.collection_amount || entry.total_amount || 0))}`
      }
      if (entry.type === 'initial') return `期初 ${entry.total_amount}`
      if (entry.type === 'sales_return') {
        const credit = NP.minus(this.orderSettleTotal(entry), Number(entry.payment_amount) || 0)
        return `退 ${NP.round(credit, 2)}`
      }
      if (entry.type === 'sales') return `本单欠 ${this.orderArrears(entry)}`
      if (Number.isFinite(arrears) && arrears) return `欠 ${arrears}`
      return ''
    },
    setBatchPrice(key, value) {
      this.$set(this.batchPrices, key, value)
    },
    queueSave() {
      setTimeout(() => this.saveSettle(), 80)
    },
    queueApply(item) {
      setTimeout(() => this.applyBatch(item), 80)
    },
    applyBatch(item) {
      const price = Number(this.batchPrices[item.key])
      if (!price || price <= 0) {
        this.$message.warning('结算单价要大于 0')
        return
      }
      let count = 0
      this.entries.forEach((entry) => {
        if (!this.canEdit(entry)) return
        ;(entry.goods || []).forEach((line) => {
          if (goodsKey(line) !== item.key) return
          this.$set(line, 'settle_input', price)
          count += 1
        })
      })
      if (!count) {
        this.$message.warning('没有可改的行')
        return
      }
      this.saveSettle()
    },
    orderArrears(entry) {
      return NP.round(NP.minus(this.orderSettleTotal(entry), Number(entry.collection_amount) || 0), 2)
    },
    initData() {
      let id = this.$route.query.id
      if (!id && this.$route.query.item) {
        try {
          id = JSON.parse(this.$route.query.item).id
        } catch (e) {
          id = undefined
        }
      }
      this.clientId = id
      if (!id) return
      this.loading = true
      arrearsReceivableLedgers({ id }).then((data) => {
        this.info = data || {}
        this.entries = ((data && data.entries) || []).map((entry, index) => ({
          ...entry,
          rowKey: `${entry.type || 'row'}-${entry.id || entry.number || index}`,
          goods: (entry.goods || []).map((g, gi) => ({
            ...g,
            id: g.id || `${index}-${gi}`,
            settle_input: g.price,
          })),
        }))
      }).finally(() => {
        this.loading = false
      })
    },
    pricePayload(type) {
      return this.entries
        .filter((entry) => {
          if (entry.type !== type || !this.canEdit(entry)) return false
          const changed = (entry.goods || []).some((item) => !priceUnchanged(item))
          const total = NP.round(Number(entry.total_amount) || 0, 2)
          return changed || this.orderSettleTotal(entry) !== total
        })
        .map((entry) => ({
          id: entry.id,
          items: (entry.goods || []).filter((item) => item.id != null && item.id !== '' && Number.isFinite(Number(item.id))).map((item) => ({
            id: Number(item.id),
            settle_price: lineUnit(item),
            settle_amount: this.lineSettleAmount(item),
          })),
        }))
        .filter((order) => order.items.length)
    },
    pendingSettle() {
      const orders = this.pricePayload('sales')
      const returns = this.pricePayload('sales_return')
      return { orders, returns, docs: orders.concat(returns) }
    },
    saveSettle(done) {
      const { orders, returns, docs } = this.pendingSettle()
      if (!docs.length) {
        if (typeof done === 'function') done()
        else this.$message.info('单价没有变化')
        return
      }
      if (docs.some((order) => order.items.some((item) => !item.settle_price || item.settle_price <= 0))) {
        this.$message.warning('单价要大于 0')
        return false
      }
      this.saving = true
      return arrearsReceivableSettle({ id: this.clientId, orders, returns }).then((data) => {
        const amount = data && data.arrears_amount
        if (amount != null) {
          try {
            sessionStorage.setItem('clientArrearsPatch', JSON.stringify({ id: this.clientId, amount }))
          } catch (e) {}
          this.$set(this.info, 'arrears_amount', amount)
          this.$message.success('欠款已改为 ' + amount)
        } else {
          this.$message.success('结算价已更新，客户欠款已按差额调整')
        }
        this.skipLeaveSave = true
        if (typeof done === 'function') done()
        else this.$router.go(-1)
        return true
      }).catch(() => false).finally(() => {
        this.saving = false
      })
    },
    goBack() {
      this.saveSettle(() => {
        this.skipLeaveSave = true
        this.$router.go(-1)
      })
    },
  },
  mounted() {
    this.initData()
  },
  beforeRouteLeave(to, from, next) {
    if (this.skipLeaveSave || this.saving) {
      next()
      return
    }
    const { docs } = this.pendingSettle()
    if (!docs.length) {
      next()
      return
    }
    const pending = this.saveSettle(() => next())
    if (pending === false) next(false)
    else if (pending && pending.then) pending.then((ok) => { if (ok === false) next(false) })
  },
}
</script>
